import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import { mergeSlotProps } from "@mui/material";
import type { FormikValues } from "formik";
import { useField, useFormikContext } from "formik";
import { get as _get } from "lodash-es";
import type { ReactNode } from "react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { TruncatedText } from "@citrine/atoms/truncated-text";

import type { FormProps } from "./form";
import { Form, useFormConfiguration } from "./form";
import * as sx from "./integrated-text-field.sx";
import type { TextFieldProps } from "./text-field";
import { isInputEnterEvent } from "./utils";

export interface IntegratedTextFieldProps<
  Shape extends FormikValues,
> extends TextFieldProps<Shape> {
  defaultEditing?: boolean;
  minRows?: number;
  onCancel?: () => void;
  onConfirm?: (newValue: string | number) => void;
  onEdit?: () => void;
  renderValue?: (value: string | number) => ReactNode;
}

export function IntegratedTextField<Shape extends FormikValues = FormikValues>({
  "aria-labelledby": ariaLabelledBy,
  className,
  defaultEditing = false,
  onCancel,
  onConfirm,
  onEdit,
  renderValue,
  slotProps,
  ...passThroughProps
}: IntegratedTextFieldProps<Shape>) {
  const {
    disabled,
    name,
    multiline = false,
    minRows = 1,
    placeholder,
    size,
  } = passThroughProps;

  const { formatMessage } = useIntl();
  const [{ value }, { error }, { setValue }] = useField<string | number>(name);
  const { submitForm, validateForm } = useFormikContext();
  const [isEditing, setIsEditing] = useState(defaultEditing);
  const { readonly, submitOnEnter } = useFormConfiguration();

  const prevValue = useRef<string | number>(value);

  const cancel = useCallback(async () => {
    setIsEditing(false);
    setValue(prevValue.current);
    await onCancel?.();
  }, [onCancel, setValue]);

  const runFormValidation = useCallback(async () => {
    /**
     * submitForm does not throw correctly for nested errors
     * so we need to gate on explicit validation
     */
    const validationErrors = await validateForm();
    const errors = _get(validationErrors, name);

    if (!errors) {
      setIsEditing(false);
    }

    return errors;
  }, [name, setIsEditing, validateForm]);

  const confirm = useCallback(async () => {
    /**
     * submitForm does not throw correctly for nested errors
     * so we need to gate on explicit validation
     */
    const errors = await runFormValidation();
    if (!errors) {
      onConfirm?.(value);
      if (submitOnEnter) {
        await submitForm();
      }
    }
  }, [onConfirm, runFormValidation, submitForm, submitOnEnter, value]);

  const onKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        cancel();
      } else if (isInputEnterEvent(e)) {
        // prevent TextField from submitting form
        e.preventDefault();
        // We only need to worry about running validation on the form and not
        // submitting. This is because the <TextField /> component already performs
        // a form submission when submitOnEnter is true and validation passes.
        await runFormValidation();
      }
    },
    [cancel, runFormValidation],
  );

  const slotPropsMod = useMemo(
    () => ({
      ...slotProps,
      input: mergeSlotProps(
        {
          endAdornment: (
            <sx.Actions active multiline={multiline} size={size}>
              {/* Cancel edit button. Resets value */}
              <sx.SquareButton
                key="cancel" // adding keys to prevent tooltips from hanging between states
                size={size}
                onClick={cancel}
                tooltip={formatMessage({
                  id: "cancel",
                })}
              >
                <ClearIcon fontSize="small" />
              </sx.SquareButton>
              {/* Confirm edit button */}
              <sx.ConfirmButton
                key="confirm"
                data-testid="submit-button"
                disabled={disabled}
                size={size}
                onClick={confirm}
                tooltip={formatMessage({
                  id: "Confirm",
                })}
                type="submit"
              >
                <CheckIcon fontSize="small" />
              </sx.ConfirmButton>
            </sx.Actions>
          ),
        },
        slotProps?.input ?? {},
      ),
    }),
    [slotProps, cancel, confirm, disabled, formatMessage, multiline, size],
  );

  const renderedValue = useMemo(
    () => renderValue?.(value) ?? value,
    [renderValue, value],
  );

  return (
    <sx.Container
      className={className}
      multiline={multiline}
      size={size}
      isEditing={isEditing}
    >
      {isEditing ? (
        <sx.TextField
          autoFocus
          fullWidth
          {...passThroughProps}
          aria-labelledby={ariaLabelledBy}
          onKeyDown={onKeyDown}
          slotProps={slotPropsMod}
        />
      ) : (
        <>
          <sx.ValueContainer
            hasError={Boolean(error)}
            minRows={minRows}
            multiline={multiline}
            size={size}
          >
            <sx.Value aria-labelledby={ariaLabelledBy} asPlaceholder={!value}>
              {multiline ? (
                renderedValue || placeholder
              ) : (
                <TruncatedText>{renderedValue || placeholder}</TruncatedText>
              )}
            </sx.Value>
            {error ? <sx.ErrorMessage>{error}</sx.ErrorMessage> : null}
          </sx.ValueContainer>
          {readonly ? null : (
            <sx.Actions multiline={multiline} size={size}>
              {/* Edit button */}
              <sx.IconButton
                data-testid={`edit-${name}-button`}
                key="edit"
                disabled={disabled}
                onClick={() => {
                  prevValue.current = value;
                  setIsEditing(true);
                  onEdit?.();
                }}
                size={size}
                tooltip={formatMessage({
                  id: "Edit",
                })}
              >
                <EditIcon fontSize="small" />
              </sx.IconButton>
            </sx.Actions>
          )}
        </>
      )}
    </sx.Container>
  );
}

IntegratedTextField.slots = {
  Actions: sx.Actions,
  Container: sx.Container,
  TextField: sx.TextField,
  ValueContainer: sx.ValueContainer,
  Value: sx.Value,
};

export function IntegratedTextForm<Shape extends FormikValues = FormikValues>({
  enableReinitialize,
  initialValues,
  readonly,
  onSubmit,
  validate,
  ...passThroughProps
}: Omit<IntegratedTextFieldProps<Shape>, "onSubmit"> &
  Pick<
    FormProps<Shape>,
    | "enableReinitialize"
    | "initialValues"
    | "readonly"
    | "onSubmit"
    | "validate"
  >) {
  return (
    <Form
      enableReinitialize={enableReinitialize}
      initialValues={initialValues}
      readonly={readonly}
      onSubmit={onSubmit}
      submitOnEnter
      validate={validate}
    >
      <IntegratedTextField {...passThroughProps} />
    </Form>
  );
}

IntegratedTextForm.slots = IntegratedTextField.slots;
